/**
 * @architecture-header 1
 * @layer module
 * @domain live
 * @responsibility Track the open intervention per student and keep the closed ones as a record.
 * @state-owner module-scoped Zustand
 * @allowed-imports Zustand; live intervention model; shared transient reset registry
 * @forbidden server entities; roles; permissions; tokens; localStorage; API client
 * @public-api live module
 * @side-effects in-memory UI updates only
 * @tests modules/live/model/intervention.test.ts
 */

import { create } from 'zustand';
import { registerWorkspaceTransientReset } from '../../../shared/lib/transient-reset';
import type { InterventionSession } from '../model/intervention';

interface InterventionState {
  sessions: readonly InterventionSession[];
  start(session: InterventionSession): void;
  patch(id: string, next: Partial<InterventionSession>): void;
  clear(): void;
}

export const useInterventions = create<InterventionState>((set) => ({
  sessions: [],
  start: (session) => set((state) => ({ sessions: [...state.sessions, session] })),
  patch: (id, next) =>
    set((state) => ({
      sessions: state.sessions.map((item) => (item.id === id ? { ...item, ...next } : item)),
    })),
  clear: () => set({ sessions: [] }),
}));

export function selectOpenSession(
  sessions: readonly InterventionSession[],
  student: string,
): InterventionSession | undefined {
  return sessions.find((session) => session.student === student && !session.endedAt);
}

registerWorkspaceTransientReset('interventions', () => useInterventions.getState().clear());
